/**
 * Utility functions for URL detection and validation
 */

/**
 * Checks if a string is a valid URL
 * Supports http, https, ftp, and common URL patterns including www.
 *
 * @param text - The string to check
 * @returns true if the string is a valid URL, false otherwise
 */
export function isValidUrl(text: string | null | undefined): boolean {
  if (!text || typeof text !== 'string') {
    return false;
  }

  // Remove whitespace
  const trimmed = text.trim();

  if (trimmed.length === 0) {
    return false;
  }

  // Try to construct a URL object - this handles most valid URLs
  try {
    const url = new URL(trimmed);
    // Check for common protocols
    return ['http:', 'https:', 'ftp:', 'ftps:'].includes(url.protocol);
  } catch {
    // If URL constructor fails, check for www. pattern without protocol
    if (trimmed.startsWith('www.')) {
      try {
        // Try with https:// prefix
        const url = new URL(`https://${trimmed}`);
        // Basic validation: should have a domain with at least one dot
        return url.hostname.includes('.');
      } catch {
        return false;
      }
    }
    return false;
  }
}

/**
 * Normalizes a URL string by adding protocol if missing
 * Useful for creating clickable links from www. URLs
 *
 * @param text - The URL string to normalize
 * @returns Normalized URL with protocol, or the original text if not a URL
 */
export function normalizeUrl(text: string | null | undefined): string {
  if (!text || typeof text !== 'string') {
    return text || '';
  }

  const trimmed = text.trim();

  // Already has protocol
  if (/^(https?|ftp|ftps):\/\//i.test(trimmed)) {
    return trimmed;
  }

  // Has www. but no protocol
  if (trimmed.startsWith('www.')) {
    return `https://${trimmed}`;
  }

  // Check if it looks like a domain (has dots and no spaces)
  if (
    trimmed.includes('.') &&
    !trimmed.includes(' ') &&
    /^[a-zA-Z0-9]/.test(trimmed)
  ) {
    // Try to validate as URL
    try {
      new URL(`https://${trimmed}`);
      return `https://${trimmed}`;
    } catch {
      // Not a valid URL pattern
      return trimmed;
    }
  }

  return trimmed;
}
